import { useEffect, useState } from "react";
import { Accordion } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import {
    CloudDownloadFill,
    ExclamationTriangleFill,
    Fire,
    CloudFill,
    QuestionCircleFill,
    Windows,
} from "react-bootstrap-icons";
import { getDownloadLinks } from "../services/SettingsService";
import { LoadingSpinner } from "../components/LoadingSpinner";

function Download() {
    const [links, setLinks] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const result = await getDownloadLinks();
                if (!cancelled) setLinks(result);
            } catch {
                // links stay null — cards render disabled
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    const mediaFireLink = links?.find(x => x.key === "mediaFireDownloadLink")?.value;
    const googleDriveLink = links?.find(x => x.key === "googleDriveDownloadLink")?.value;

    return (
        <div className="download-page">

            {/* Hero */}
            <div className="download-hero">
                <div className="download-hero-icon">
                    <CloudDownloadFill />
                </div>
                <h2>Download Paragon Conquer</h2>
                <span className="download-platform-badge">
                    <Windows /> Windows PC
                </span>
            </div>

            {/* Download cards */}
            {loading ? (
                <LoadingSpinner message="Loading download links…" />
            ) : (
                <div className="download-options">
                    <a
                        href={mediaFireLink || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className={`download-option-card${!mediaFireLink ? " disabled" : ""}`}
                    >
                        <div className="doc-icon"><Fire /></div>
                        <div className="doc-name">MediaFire</div>
                        <div className="doc-desc">Fast, free direct download via MediaFire</div>
                        <span className="doc-btn">Download</span>
                    </a>

                    <a
                        href={googleDriveLink || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className={`download-option-card${!googleDriveLink ? " disabled" : ""}`}
                    >
                        <div className="doc-icon"><CloudFill /></div>
                        <div className="doc-name">Google Drive</div>
                        <div className="doc-desc">Official mirror hosted on Google Drive</div>
                        <span className="doc-btn">Download</span>
                    </a>
                </div>
            )}

            {/* Flash Player notice */}
            <div className="download-flash-notice">
                <div className="dfn-header">
                    <ExclamationTriangleFill size={18} />
                    <h3>Adobe has discontinued support for Flash Player</h3>
                </div>
                <div className="dfn-body">
                    <p>You may experience an error while launching the game.</p>
                    <p>Try each method below — one of them will fix it.</p>
                    <ul>
                        <li>
                            <a href="https://www.techspot.com/downloads/524-flash-player-pro.html" target="_blank" rel="noreferrer">
                                Method 1
                            </a>
                            &nbsp;— Download and install
                        </li>
                        <li>
                            <a href="https://www.mediafire.com/file/txxoy3cw87euvyy/NewFlash.7z/file" target="_blank" rel="noreferrer">
                                Method 2
                            </a>
                            &nbsp;— Extract and replace in client folder
                        </li>
                        <li>
                            <a href="https://www.mediafire.com/file/k7rmmd22m71h6tm/OldFlash.7z/file" target="_blank" rel="noreferrer">
                                Method 3
                            </a>
                            &nbsp;— Extract and replace in client folder
                        </li>
                    </ul>
                </div>
            </div>

            {/* FAQ */}
            <div className="download-faq-header">
                <QuestionCircleFill size={18} />
                <h4>Common Client Problems</h4>
            </div>
            <Accordion flush className="download-accordion">
                <Accordion.Item eventKey="1">
                    <Accordion.Header>How do I launch the game?</Accordion.Header>
                    <Accordion.Body>
                        Run <strong>Autopatch.exe</strong> from your install directory.
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="2">
                    <Accordion.Header>I need assistance installing the game</Accordion.Header>
                    <Accordion.Body>
                        <h3>To get assistance:</h3>
                        <ol>
                            <li>
                                Check our&nbsp;
                                <LinkContainer to="/faq">
                                    <span className="fake-link">FAQ</span>
                                </LinkContainer>
                                &nbsp;for common problems
                            </li>
                            <li>
                                Ask for help on our&nbsp;
                                <a href="https://discord.gg/rCQ5Bask8S" target="_blank" rel="noreferrer">Discord</a>
                                &nbsp;if you need further assistance
                            </li>
                        </ol>
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="3">
                    <Accordion.Header>How do I add exceptions to my antivirus?</Accordion.Header>
                    <Accordion.Body>
                        <h3>Adding Antivirus Exceptions:</h3>
                        <p>Each antivirus handles exclusions differently. Common guides:</p>
                        <ul>
                            <li><a href="https://youtu.be/BonLkFNnO9w" target="_blank" rel="noreferrer">Windows Defender (Win 10)</a></li>
                            <li><a href="https://youtu.be/eoRSxbMPWRc" target="_blank" rel="noreferrer">Avast</a></li>
                            <li><a href="https://youtu.be/9NbfmhJAiDs" target="_blank" rel="noreferrer">ESET</a></li>
                            <li><a href="https://youtu.be/E2VA62rYTag" target="_blank" rel="noreferrer">Kaspersky</a></li>
                        </ul>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

        </div>
    );
}

export { Download };
